import type {NativeMessage, NativeHistoryPage} from '../../../src/native-hermes-types';

type History = {displayHistory: NativeMessage[]; historyPage: NativeHistoryPage};
function identity(row: NativeMessage): string {
  if (row.logical_id) return `logical:${row.logical_id}`;
  if (row.row_id !== undefined) return `row:${row.row_id}`;
  throw new Error('The native message has no history identity.');
}

/** Saved row identities own deduplication; native timestamps own cross-reader ordering. */
export function mergeNativeMessages(current: readonly NativeMessage[], incoming: readonly NativeMessage[]): NativeMessage[] {
  const rows = new Map(current.map(row => [identity(row), row]));
  for (const row of incoming) rows.set(identity(row), row);
  const timestamp = (row: NativeMessage) => {
    if (typeof row.timestamp === 'number') return row.timestamp;
    if (typeof row.timestamp === 'string') {
      const value = Date.parse(row.timestamp) / 1000;
      if (Number.isFinite(value)) return value;
    }
    throw new Error('The saved message has no valid native timestamp.');
  };
  return [...rows.values()].sort((left,right) => timestamp(left)-timestamp(right)
    || (typeof left.row_id==='number'&&typeof right.row_id==='number'?left.row_id-right.row_id:identity(left).localeCompare(identity(right))));
}

/** Merge only native identities, never text matching or another transcript store. */
export function mergeHistoryPage(current: History, update: History, direction: 'older' | 'newer'): History {
  const displayHistory = direction === 'older'
    ? mergeNativeMessages(update.displayHistory, current.displayHistory)
    : mergeNativeMessages(current.displayHistory,update.displayHistory);
  return {displayHistory, historyPage: {...update.historyPage,
    before: direction === 'older' ? update.historyPage.before : current.historyPage.before ?? update.historyPage.before,
    after: direction === 'older' ? current.historyPage.after : update.historyPage.after ?? current.historyPage.after,
    hasOlder: direction === 'older' ? update.historyPage.hasOlder : current.historyPage.hasOlder,
    hasNewer: direction === 'older' ? current.historyPage.hasNewer : update.historyPage.hasNewer}};
}
